import React, { useState, useEffect } from 'react';
import { addBookingToFirestore, updateBookingInFirestore, getAllBookingsFromFirestore } from '../utils/firestoreBooking';
import { updatePaymentStatusDirect } from '../utils/directPaymentUpdate';
import { sendBookingConfirmationEmail } from '../utils/emailService';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import DormitoryLayout from '../booking-components/DormitoryLayout';
import Invoice from '../components/Invoice';
import '../components/BookingModalAdmin.css';
import { FaArrowLeft, FaBed } from 'react-icons/fa';
import { generateInvoicePDF } from '../utils/pdfGenerator';
import { toast } from 'react-toastify';

export default function BookingAdminPage() {
  const [showBedSelector, setShowBedSelector] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { mode = 'add', booking = null } = location.state || {};
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    checkIn: '',
    checkOut: '',
    bedType: 'Male Dormitory',
    selectedBeds: [],
    amount: 0,
    status: 'confirmed',
    payment: 'paid',
    extraCharges: 0,
    nights: 1,
    beds: 1,
    package: 'Regular Plan - 12-24 Hours',
    pricePerBed: 599,
    applyConvenienceFee: true,
    convenienceFeeRate: 0.05,
    amountPaid: 0,
    total: 599,
  });

  useEffect(() => {
    if (mode === 'edit' || mode === 'view') {
      console.log('BookingAdminPage: Setting form data for', mode, 'mode with booking:', booking);
      setForm({
        name: booking?.guestName || booking?.name || '',
        email: booking?.guestEmail || booking?.email || '',
        phone: booking?.guestPhone || booking?.phone || '',
        checkIn: booking?.checkIn || '',
        checkOut: booking?.checkOut || '',
        bedType: booking?.bedType || 'Male Dormitory',
        selectedBeds: booking?.selectedBeds || [],
        amount: booking?.total || booking?.amount || 0,
        status: booking?.status || 'confirmed',
        payment: booking?.payment || 'paid',
        extraCharges: booking?.extraCharges || 0,
        nights: booking?.nights || 1,
        beds: booking?.beds || booking?.selectedBeds?.length || 1,
        package: booking?.package || booking?.bookingData?.package?.name || 'Regular Plan - 12-24 Hours',
        pricePerBed: booking?.pricePerBed || booking?.bookingData?.package?.price || 599,
        applyConvenienceFee: booking?.applyConvenienceFee !== undefined ? booking.applyConvenienceFee : true,
        convenienceFeeRate: booking?.convenienceFeeRate ?? 0.05,
        amountPaid: booking?.amountPaid || 0,
        total: booking?.total || booking?.amount || 599,
      });
    } else {
      setForm({
        name: '',
        email: '',
        phone: '',
        checkIn: '',
        checkOut: '',
        bedType: 'Male Dormitory',
        selectedBeds: [],
        amount: 0,
        status: 'pending',
        payment: 'pending',
        extraCharges: [],
        applyConvenienceFee: true,
        convenienceFeeRate: 0.05,
        amountPaid: 0,
      });
    }
  }, [mode, booking]);

  const [unavailableBedIds, setUnavailableBedIds] = useState([]);
  useEffect(() => {
    if (!form.checkIn || !form.checkOut) {
      setUnavailableBedIds([]);
      return;
    }
    
    const fetchBookings = async () => {
      try {
        const allBookings = await getAllBookingsFromFirestore();
        const thisBookingId = booking?.id;
        const checkIn = new Date(form.checkIn);
        const checkOut = new Date(form.checkOut);
        const overlapping = allBookings.filter(b => {
          if (thisBookingId && b.id === thisBookingId) return false;
          if (!b.checkIn || !b.checkOut) return false;
          const bCheckIn = new Date(b.checkIn);
          const bCheckOut = new Date(b.checkOut);
          return (checkIn < bCheckOut) && (checkOut > bCheckIn);
        });
        const bedsBooked = overlapping.flatMap(b => (b.selectedBeds ? b.selectedBeds.map(bed => bed.id) : []));
        setUnavailableBedIds(bedsBooked);
      } catch (error) {
        console.error('Error fetching bookings:', error);
        setUnavailableBedIds([]);
      }
    };
    
    fetchBookings();
  }, [form.checkIn, form.checkOut, booking]);

  const isView = mode === 'view';

  const getPackagePrice = (packageName) => {
    const prices = {
      'Hourly Plan - 1 Hour': 199,
      'Hourly Plan - 3 Hours': 349,
      'Hourly Plan - 5 Hours': 499,
      'Regular Plan - 12-24 Hours': 599,
      'Weekly Plan - 7 Days': 3499,
      'Monthly Plan - 30 Days': 11999
    };
    return prices[packageName] || 599;
  };

  const calculateCheckoutDate = (checkInDate, packageName) => {
    if (!checkInDate) return '';
    
    const checkIn = new Date(checkInDate);
    let checkOut = new Date(checkIn);
    
    switch (packageName) {
      case 'Hourly Plan - 1 Hour':
        checkOut.setHours(checkIn.getHours() + 1);
        break;
      case 'Hourly Plan - 3 Hours':
        checkOut.setHours(checkIn.getHours() + 3);
        break;
      case 'Hourly Plan - 5 Hours':
        checkOut.setHours(checkIn.getHours() + 5);
        break;
      case 'Regular Plan - 12-24 Hours':
        checkOut.setDate(checkIn.getDate() + 1);
        break;
      case 'Weekly Plan - 7 Days':
        checkOut.setDate(checkIn.getDate() + 7);
        break;
      case 'Monthly Plan - 30 Days':
        checkOut.setMonth(checkIn.getMonth() + 1);
        break;
      default:
        checkOut.setDate(checkIn.getDate() + 1);
    }
    
    return checkOut.toISOString().split('T')[0];
  };

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(prev => {
      const updated = { ...prev, [name]: name === 'applyConvenienceFee' ? !!e.target.checked : (name === 'pricePerBed' || name === 'beds' || name === 'amountPaid' ? Number(value) : value) };
      
      // If package changes, update price and checkout date
      if (name === 'package') {
        updated.pricePerBed = getPackagePrice(value);
        if (prev.checkIn) {
          updated.checkOut = calculateCheckoutDate(prev.checkIn, value);
        }
        // Update nights based on new dates
        if (prev.checkIn && updated.checkOut) {
          const n = Math.ceil((new Date(updated.checkOut) - new Date(prev.checkIn)) / (1000 * 60 * 60 * 24)) || 1;
          updated.nights = n;
        }
        // Recalculate total amount
        const beds = parseInt(prev.beds) || 1;
        const isHourly = value.includes('Hourly Plan');
        const isWeekly = value.includes('Weekly Plan');
        const isMonthly = value.includes('Monthly Plan');
        const nights = (isHourly || isWeekly || isMonthly) ? 1 : Math.ceil((new Date(updated.checkOut) - new Date(prev.checkIn)) / (1000 * 60 * 60 * 24)) || 1;
        const subtotal = updated.pricePerBed * beds * ((isHourly || isWeekly || isMonthly) ? 1 : nights);
        const extraChargesTotal = (prev.extraCharges || []).reduce((sum, charge) => sum + (parseFloat(charge.amount) || 0), 0);
        const convenienceFee = updated.applyConvenienceFee ? Math.round(subtotal * (prev.convenienceFeeRate ?? 0.05)) : 0;
        updated.amount = subtotal + extraChargesTotal + convenienceFee;
      }
      
      // If check-in date changes, update checkout date based on package
      if (name === 'checkIn') {
        const packageName = prev.package || 'Regular Plan - 12-24 Hours';
        updated.checkOut = calculateCheckoutDate(value, packageName);
        // Update nights
        updated.nights = (() => {
          const isHourly = packageName.includes('Hourly Plan');
          const isWeekly = packageName.includes('Weekly Plan');
          const isMonthly = packageName.includes('Monthly Plan');
          if (isHourly || isWeekly || isMonthly) return 1;
          return Math.ceil((new Date(updated.checkOut) - new Date(value)) / (1000 * 60 * 60 * 24)) || 1;
        })();
        // Recalculate total amount
        const beds = parseInt(prev.beds) || 1;
        const isHourly = packageName.includes('Hourly Plan');
        const isWeekly = packageName.includes('Weekly Plan');
        const isMonthly = packageName.includes('Monthly Plan');
        const nights = (isHourly || isWeekly || isMonthly) ? 1 : Math.ceil((new Date(updated.checkOut) - new Date(value)) / (1000 * 60 * 60 * 24)) || 1;
        const subtotal = (prev.pricePerBed || getPackagePrice(packageName)) * beds * ((isHourly || isWeekly || isMonthly) ? 1 : nights);
        const extraChargesTotal = (prev.extraCharges || []).reduce((sum, charge) => sum + (parseFloat(charge.amount) || 0), 0);
        const convenienceFee = (prev.applyConvenienceFee ? Math.round(subtotal * (prev.convenienceFeeRate ?? 0.05)) : 0);
        updated.amount = subtotal + extraChargesTotal + convenienceFee;
      }

      // If check-out date changes manually, update nights and recalc
      if (name === 'checkOut') {
        const packageName = prev.package || 'Regular Plan - 12-24 Hours';
        // Update nights (non-hourly/weekly/monthly only)
        const isHourly = packageName.includes('Hourly Plan');
        const isWeekly = packageName.includes('Weekly Plan');
        const isMonthly = packageName.includes('Monthly Plan');
        const nights = (isHourly || isWeekly || isMonthly) ? 1 : Math.ceil((new Date(value) - new Date(prev.checkIn)) / (1000 * 60 * 60 * 24)) || 1;
        updated.nights = nights;
        // Recalculate total amount
        const beds = parseInt(prev.beds) || 1;
        const pricePerBed = prev.pricePerBed || getPackagePrice(packageName);
        const subtotal = pricePerBed * beds * ((isHourly || isWeekly || isMonthly) ? 1 : nights);
        const extraChargesTotal = (prev.extraCharges || []).reduce((sum, charge) => sum + (parseFloat(charge.amount) || 0), 0);
        const convenienceFee = (prev.applyConvenienceFee ? Math.round(subtotal * (prev.convenienceFeeRate ?? 0.05)) : 0);
        updated.amount = subtotal + extraChargesTotal + convenienceFee;
      }
      
      // If extra charges change, recalculate amount
      if (name === 'extraCharges') {
        const beds = parseInt(prev.beds) || 1;
        const packageName = prev.package || 'Regular Plan - 12-24 Hours';
        const pricePerBed = prev.pricePerBed || getPackagePrice(packageName);
        const isHourly = packageName.includes('Hourly Plan');
        const isWeekly = packageName.includes('Weekly Plan');
        const isMonthly = packageName.includes('Monthly Plan');
        const nights = (isHourly || isWeekly || isMonthly) ? 1 : Math.ceil((new Date(prev.checkOut) - new Date(prev.checkIn)) / (1000 * 60 * 60 * 24)) || 1;
        const subtotal = pricePerBed * beds * ((isHourly || isWeekly || isMonthly) ? 1 : nights);
        const extraChargesTotal = (value || []).reduce((sum, charge) => sum + (parseFloat(charge.amount) || 0), 0);
        const convenienceFee = updated.applyConvenienceFee ? Math.round(subtotal * (prev.convenienceFeeRate ?? 0.05)) : 0;
        updated.amount = subtotal + extraChargesTotal + convenienceFee;
      }

      // If beds changes, recalculate amount
      if (name === 'beds') {
        const beds = parseInt(value) || 1;
        const packageName = prev.package || 'Regular Plan - 12-24 Hours';
        const pricePerBed = prev.pricePerBed || getPackagePrice(packageName);
        const isHourly = packageName.includes('Hourly Plan');
        const isWeekly = packageName.includes('Weekly Plan');
        const isMonthly = packageName.includes('Monthly Plan');
        const nights = (isHourly || isWeekly || isMonthly) ? 1 : Math.ceil((new Date(prev.checkOut) - new Date(prev.checkIn)) / (1000 * 60 * 60 * 24)) || 1;
        const subtotal = pricePerBed * beds * ((isHourly || isWeekly || isMonthly) ? 1 : nights);
        const extraChargesTotal = (prev.extraCharges || []).reduce((sum, charge) => sum + (parseFloat(charge.amount) || 0), 0);
        const convenienceFee = updated.applyConvenienceFee ? Math.round(subtotal * (prev.convenienceFeeRate ?? 0.05)) : 0;
        updated.amount = subtotal + extraChargesTotal + convenienceFee;
        
        // Reset selected beds if exceeding new limit
        if (prev.selectedBeds && prev.selectedBeds.length > beds) {
          updated.selectedBeds = prev.selectedBeds.slice(0, beds);
        }
      }

      // If price per bed or fee toggle changes, recalc
      if (name === 'pricePerBed' || name === 'applyConvenienceFee') {
        const packageName = prev.package || 'Regular Plan - 12-24 Hours';
        const isHourly = packageName.includes('Hourly Plan');
        const isWeekly = packageName.includes('Weekly Plan');
        const isMonthly = packageName.includes('Monthly Plan');
        const nights = (isHourly || isWeekly || isMonthly) ? 1 : Math.ceil((new Date(prev.checkOut) - new Date(prev.checkIn)) / (1000 * 60 * 60 * 24)) || 1;
        const beds = parseInt(prev.beds) || 1;
        const price = Number(name === 'pricePerBed' ? value : prev.pricePerBed) || 0;
        const extraChargesTotal = (prev.extraCharges || []).reduce((sum, charge) => sum + (parseFloat(charge.amount) || 0), 0);
        const subtotal = price * beds * ((isHourly || isWeekly || isMonthly) ? 1 : nights);
        const feeRate = prev.convenienceFeeRate ?? 0.05;
        const convenienceFee = (name === 'applyConvenienceFee' ? !!e.target.checked : prev.applyConvenienceFee) ? Math.round(subtotal * feeRate) : 0;
        updated.amount = subtotal + extraChargesTotal + convenienceFee;
      }
      
      return updated;
    });
  }

  async function handleSubmit(e) {
    try {
      e.preventDefault();
      console.log('🔥 BUTTON CLICKED - handleSubmit fired');
      console.log('🔥 Mode:', mode);
      console.log('🔥 Booking object:', booking);
      console.log('🔥 Form data:', form);
      
      // Add validation logging
      console.log('Form validation check:');
      console.log('- selectedBeds:', form.selectedBeds);
      console.log('- selectedBeds length:', form.selectedBeds?.length);
      console.log('- name:', form.name);
      console.log('- email:', form.email);
      console.log('- checkIn:', form.checkIn);
      console.log('- checkOut:', form.checkOut);
      
      if (!form.selectedBeds || form.selectedBeds.length === 0) {
        console.log('🚨 VALIDATION FAILED: No beds selected');
        alert('Please select at least one bed.');
        return;
      }
      
      if (!form.name || !form.email || !form.checkIn || !form.checkOut) {
        console.log('🚨 VALIDATION FAILED: Missing required fields');
        alert('Please fill in all required fields (Name, Email, Check-in, Check-out).');
        return;
      }
      
      console.log('✅ VALIDATION PASSED - Proceeding with booking save');
      // Add booking to storage
      if (mode === 'add') {
        // For add mode, create new booking
        try {
          // Generate unique booking ID to prevent duplicates
          const bookingId = `SKYS${Date.now().toString(36).substr(2, 8).toUpperCase()}`;
          
          // Check if booking already exists to prevent duplicates
          const existingBookings = await getAllBookingsFromFirestore();
          const duplicateBooking = existingBookings.find(b => b.id === bookingId);
          if (duplicateBooking) {
            console.log('Duplicate booking ID detected, generating new one');
            const newBookingId = `SKYS${(Date.now() + Math.random() * 1000).toString(36).substr(2, 8).toUpperCase()}`;
            console.log('New booking ID:', newBookingId);
          }
          
          const packageName = form.package || 'Regular Plan - 12-24 Hours';
          const packageDuration = packageName.includes('Hourly') ? '2-12 Hours' : 
                                  packageName.includes('Weekly') ? '7 Days' : 
                                  packageName.includes('Monthly') ? '30 Days' : '12-24 Hours';
          const beds = parseInt(form.beds) || form.selectedBeds?.length || 1;
          const isHourly = packageName.includes('Hourly Plan');
          const isWeekly = packageName.includes('Weekly Plan');
          const isMonthly = packageName.includes('Monthly Plan');
          const nights = (isHourly || isWeekly || isMonthly) ? 1 : Math.ceil((new Date(form.checkOut) - new Date(form.checkIn)) / (1000 * 60 * 60 * 24)) || 1;
          
          const unifiedBooking = {
            id: bookingId,
            userId: null, // Admin bookings may not have a userId
            userName: null, // Admin bookings may not have a userName
            guestName: form.name || '',
            guestEmail: form.email || '',
            guestPhone: form.phone || '',
            checkIn: form.checkIn || '',
            checkOut: form.checkOut || '',
            bedType: form.bedType || 'Male Dormitory',
            selectedBeds: form.selectedBeds || [],
            beds: beds,
            nights: nights,
            pricePerBed: form.pricePerBed || 599, // Ensure this is never undefined
            amount: form.amount || 0,
            total: form.amount || 0,
            applyConvenienceFee: form.applyConvenienceFee ?? true,
            convenienceFeeRate: form.convenienceFeeRate ?? 0.05,
            amountPaid: form.amountPaid || 0,
            status: form.status || 'confirmed',
            payment: form.payment || 'paid',
            extraCharges: form.extraCharges || [],
            createdAt: new Date().toISOString(),
            paymentMethod: 'offline', // Admin bookings are offline
            bookingData: {
              start: form.checkIn || '',
              end: form.checkOut || '',
              beds: beds,
              nights: nights,
              total: form.amount || 0,
              package: {
                name: packageName,
                duration: packageDuration,
                price: form.pricePerBed || 599 // Ensure this is never undefined
              }
            }
          };
          console.log('About to save booking to Firestore:', unifiedBooking);
          
          // Save to Firestore first (primary storage)
          const firestoreId = await addBookingToFirestore(unifiedBooking);
          console.log('✅ Booking saved to Firestore with ID:', firestoreId);
          
          // Only save to localStorage as backup if Firestore succeeds
          try {
            const existingBookings = JSON.parse(localStorage.getItem('userBookings') || '[]');
            
            // Check for duplicates in localStorage before adding
            const existingIndex = existingBookings.findIndex(b => b.id === unifiedBooking.id);
            if (existingIndex === -1) {
              existingBookings.push(unifiedBooking);
              localStorage.setItem('userBookings', JSON.stringify(existingBookings));
              console.log('✅ Booking also saved to localStorage as backup');
            } else {
              console.log('⚠️ Booking already exists in localStorage, skipping duplicate');
            }
          } catch (localError) {
            console.error('❌ Failed to save to localStorage:', localError);
            // Don't fail the entire operation if localStorage fails
          }
          
          // Send confirmation email to guest
          try {
            console.log('Sending booking confirmation email to:', form.email);
            await sendBookingConfirmationEmail({
              id: unifiedBooking.id,
              guestName: form.name,
              guestEmail: form.email,
              guestPhone: form.phone,
              checkIn: form.checkIn,
              checkOut: form.checkOut,
              bedType: form.bedType,
              selectedBeds: form.selectedBeds,
              beds: beds,
              nights: nights,
              total: form.amount,
              amount: form.amount,
              bookingId: unifiedBooking.id,
              extraCharges: form.extraCharges || [],
              bookingData: {
                start: form.checkIn,
                end: form.checkOut,
                beds: beds,
                nights: nights,
                total: form.amount,
                package: {
                  plan: packageName,
                  duration: packageDuration,
                  price: form.pricePerBed || 599
                }
              },
              selectedPackage: {
                plan: packageName,
                duration: packageDuration,
                price: form.pricePerBed || 599
              }
            });
            console.log('✅ Booking confirmation email sent successfully');
            toast.success(`Booking created successfully! ID: ${unifiedBooking.id}`);
          } catch (emailError) {
            console.error('❌ Failed to send confirmation email:', emailError);
            toast.success(`Booking created successfully! ID: ${unifiedBooking.id} (Email sending failed)`);
          }
        } catch (err) {
          console.error('❌ Error in addBookingToFirestore:', err);
          toast.error('Failed to save booking to Firebase. Please try again.');
          return;
        }
      } else if (mode === 'edit' && booking) {
        // For edit mode, update existing booking
        try {
          console.log('BookingAdminPage: Updating existing booking:', booking.id);
          console.log('BookingAdminPage: Form payment status:', form.payment);
          console.log('BookingAdminPage: Original payment status:', booking.payment);
          
          const updateData = {
            guestName: form.name || '',
            guestEmail: form.email || '',
            guestPhone: form.phone || '',
            checkIn: form.checkIn || '',
            checkOut: form.checkOut || '',
            bedType: form.bedType || 'Male Dormitory',
            selectedBeds: form.selectedBeds || [],
            beds: parseInt(form.beds) || form.selectedBeds?.length || 1,
            amount: form.amount || 0,
            total: form.amount || 0,
            applyConvenienceFee: form.applyConvenienceFee ?? true,
            convenienceFeeRate: form.convenienceFeeRate ?? 0.05,
            amountPaid: form.amountPaid || 0,
            status: form.status || 'confirmed',
            payment: form.payment || 'pending',
            extraCharges: form.extraCharges || [],
            updatedAt: new Date().toISOString()
          };
          
          console.log('BookingAdminPage: Update data:', updateData);
          
          // Check if payment status changed
          const paymentChanged = form.payment !== booking.payment;
          console.log('BookingAdminPage: Payment status changed?', paymentChanged);
          
          // Check if booking exists in Firestore or localStorage only
          const existingBookings = JSON.parse(localStorage.getItem('userBookings') || '[]');
          const localBookingIndex = existingBookings.findIndex(localBooking => localBooking.id === booking.id);
          const isLocalStorageOnly = localBookingIndex !== -1;
          
          console.log('BookingAdminPage: Debug localStorage detection:');
          console.log('- Booking ID:', booking.id);
          console.log('- localStorage bookings count:', existingBookings.length);
          console.log('- Found in localStorage index:', localBookingIndex);
          console.log('- Booking source:', booking.source);
          console.log('- Is localStorage only?', isLocalStorageOnly);
          
          if (isLocalStorageOnly) {
            console.log('BookingAdminPage: Detected localStorage-only booking, updating localStorage directly');
            try {
              existingBookings[localBookingIndex] = { ...existingBookings[localBookingIndex], ...updateData };
              localStorage.setItem('userBookings', JSON.stringify(existingBookings));
              console.log('✅ BookingAdminPage: localStorage-only booking updated successfully');
            } catch (localError) {
              console.error('❌ BookingAdminPage: localStorage update failed:', localError.message);
              throw new Error('Failed to update localStorage-only booking');
            }
          } else {
            // Handle Firestore bookings with payment status changes
            if (paymentChanged) {
              console.log('BookingAdminPage: Using direct payment update for payment status change');
              try {
                // EMERGENCY FIX: Force localStorage update for orphaned bookings
                console.log('BookingAdminPage: Attempting payment update with emergency fallback');
                
                try {
                  await updatePaymentStatusDirect(booking.id, form.payment);
                  console.log('✅ BookingAdminPage: Payment status updated successfully');
                } catch (directUpdateError) {
                  console.log('⚠️ BookingAdminPage: Direct update failed, using emergency localStorage fix');
                  
                  // EMERGENCY: Create/update booking in localStorage directly
                  const localBookings = JSON.parse(localStorage.getItem('userBookings') || '[]');
                  let bookingIndex = localBookings.findIndex(b => b.id === booking.id);
                  
                  if (bookingIndex === -1) {
                    // Booking doesn't exist in localStorage, add it
                    const newBooking = {
                      ...booking,
                      ...updateData,
                      source: 'localStorage',
                      emergencyFix: true,
                      fixedAt: new Date().toISOString()
                    };
                    localBookings.push(newBooking);
                    console.log('📦 BookingAdminPage: Added orphaned booking to localStorage');
                  } else {
                    // Update existing booking
                    localBookings[bookingIndex] = {
                      ...localBookings[bookingIndex],
                      ...updateData,
                      emergencyFix: true,
                      fixedAt: new Date().toISOString()
                    };
                    console.log('📝 BookingAdminPage: Updated existing booking in localStorage');
                  }
                  
                  localStorage.setItem('userBookings', JSON.stringify(localBookings));
                  console.log('✅ BookingAdminPage: Emergency localStorage fix completed');
                }
                
                // Update other fields separately if needed
                const otherData = { ...updateData };
                delete otherData.payment; // Remove payment from other updates
                
                if (Object.keys(otherData).length > 0) {
                  try {
                    await updateBookingInFirestore(booking.id, otherData);
                    console.log('✅ BookingAdminPage: Other fields updated in Firestore');
                  } catch (otherFieldsError) {
                    console.log('⚠️ BookingAdminPage: Firestore update for other fields failed, updating localStorage');
                    // Update localStorage for other fields as fallback
                    if (localBookingIndex !== -1) {
                      existingBookings[localBookingIndex] = { ...existingBookings[localBookingIndex], ...otherData };
                      localStorage.setItem('userBookings', JSON.stringify(existingBookings));
                      console.log('✅ BookingAdminPage: Other fields updated in localStorage as fallback');
                    }
                  }
                }
                
                // Always sync localStorage regardless
                try {
                  if (localBookingIndex !== -1) {
                    existingBookings[localBookingIndex] = { ...existingBookings[localBookingIndex], ...updateData };
                    localStorage.setItem('userBookings', JSON.stringify(existingBookings));
                    console.log('✅ BookingAdminPage: localStorage synced successfully');
                  }
                } catch (localError) {
                  console.error('⚠️ BookingAdminPage: localStorage sync failed, but main update succeeded');
                }
                
              } catch (paymentError) {
                console.error('❌ BookingAdminPage: Payment update failed completely:', paymentError.message);
                throw new Error('Failed to update payment status');
              }
            } else {
              // Normal update for non-payment changes
              try {
                await updateBookingInFirestore(booking.id, updateData);
                
                // Update localStorage to keep it in sync
                try {
                  if (localBookingIndex !== -1) {
                    existingBookings[localBookingIndex] = { ...existingBookings[localBookingIndex], ...updateData };
                    localStorage.setItem('userBookings', JSON.stringify(existingBookings));
                    console.log('✅ BookingAdminPage: localStorage also updated for sync');
                  }
                } catch (localError) {
                  console.error('⚠️ BookingAdminPage: localStorage sync failed, but Firestore update succeeded');
                }
                
              } catch (firestoreError) {
                console.error('❌ BookingAdminPage: Firestore update failed:', firestoreError.message);
                
                // If Firestore fails, try localStorage as fallback
                try {
                  if (localBookingIndex !== -1) {
                    existingBookings[localBookingIndex] = { ...existingBookings[localBookingIndex], ...updateData };
                    localStorage.setItem('userBookings', JSON.stringify(existingBookings));
                    console.log('✅ BookingAdminPage: localStorage update successful as fallback');
                  } else {
                    throw new Error('Booking not found in localStorage either');
                  }
                } catch (localError) {
                  console.error('❌ BookingAdminPage: Both Firestore and localStorage updates failed');
                  throw new Error('Failed to update booking in both Firestore and localStorage');
                }
              }
            }
          }
          
          console.log('BookingAdminPage: Booking updated successfully');
          toast.success('Booking updated successfully');
          
          // Force refresh the parent dashboard to show updated booking
          setTimeout(() => {
            window.location.reload();
          }, 1000);
          
        } catch (err) {
          console.error('BookingAdminPage: Error updating booking:', err);
          toast.error('Failed to update booking. Please try again.');
          return;
        }
      }
      navigate('/admin/dashboard', { state: { form, mode } });
    } catch (error) {
      alert('Unexpected error occurred. See console for details.');
      console.error('handleSubmit error:', error);
    }
  }

  function handleDownloadInvoice() {
    const nights = Math.ceil((new Date(form.checkOut) - new Date(form.checkIn)) / (1000 * 60 * 60 * 24));
    const beds = form.selectedBeds?.length || 1;
    const totalAmount = form.amount || 0;
    
    // Use the actual price per bed from form
    const baseRate = form.pricePerBed || 599;
    
    // Interactive options for invoice output
    let forceAllBeds = false;
    let applyFee = form.applyConvenienceFee ?? true;
    let amountPaidOverride = form.amountPaid || 0;
    try {
      forceAllBeds = window.confirm('Show "All beds" on the invoice instead of listing numbers?');
      applyFee = window.confirm('Apply convenience fee on this invoice?');
      const paidInput = window.prompt('Enter Amount Paid (leave blank for 0):', String(amountPaidOverride || ''));
      if (paidInput !== null && paidInput !== '') {
        const val = parseFloat(paidInput);
        if (!isNaN(val) && val >= 0) amountPaidOverride = val;
      }
    } catch (e) {
      // If prompts not available, fall back to form values
    }
    
    const bookingDetails = {
      bookingName: form.name,
      guestName: form.name,
      bookingId: booking?.id || 'N/A',
      checkInDate: form.checkIn ? new Date(form.checkIn).toLocaleDateString() : 'N/A',
      checkOutDate: form.checkOut ? new Date(form.checkOut).toLocaleDateString() : 'N/A',
      beds: beds,
      nights: nights > 0 ? nights : 1,
      bedNumbers: form.selectedBeds || [],
      ratePerNight: baseRate,
      taxRate: 0, // No tax, only convenience fee
      extraCharges: form.extraCharges || [],
      guests: beds,
      bedType: form.bedType,
      bedNumbers: form.selectedBeds || [],
      package: form.package ? {
        plan: (form.package || 'Regular Plan - 12-24 Hours').split(' - ')[0],
        duration: (form.package || 'Regular Plan - 12-24 Hours').split(' - ')[1],
        price: baseRate
      } : null,
      packageInfo: form.package ? {
        plan: (form.package || 'Regular Plan - 12-24 Hours').split(' - ')[0],
        duration: (form.package || 'Regular Plan - 12-24 Hours').split(' - ')[1],
        price: baseRate
      } : null,
      totalAmount: totalAmount,
      applyConvenienceFee: !!applyFee,
      convenienceFeeRate: form.convenienceFeeRate ?? 0.05,
      amountPaid: amountPaidOverride || 0,
      forceAllBeds,
      guestDetails: {
        name: form.name,
        email: form.email,
        phone: form.phone
      }
    };
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Invoice - ${bookingDetails.bookingId}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
            .invoice-container { max-width: 800px; margin: 0 auto; }
          </style>
        </head>
        <body>
          <div id="invoice-root"></div>
        </body>
      </html>
    `);
    setTimeout(() => {
      const rootDiv = printWindow.document.getElementById('invoice-root');
      // Render Invoice to new window
      import('react-dom/client').then(({ createRoot }) => {
        const root = createRoot(rootDiv);
        root.render(React.createElement(Invoice, { bookingDetails }));
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 1000);
      });
    }, 100);
  }

  return (
    <>
      <Header />
      <div className="booking-admin-page-container">
        <button className="back-to-dashboard" onClick={() => navigate('/admin/dashboard')} style={{ margin: '18px 0 0 18px', background: 'none', border: 'none', color: '#3f51b5', fontSize: '1.1rem', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
          <FaArrowLeft style={{ marginRight: 7 }} /> Back to Dashboard
        </button>
        <div className="booking-modal booking-admin-fullpage">
          <div className="modal-content">
            <div className="modal-form-section">
              <div className="modal-title">
                {mode==='add'?'Add Booking':mode==='edit'?'Edit Booking':'Booking Details'}
              </div>
              <form onSubmit={handleSubmit} className="booking-form">
                <div>
                  <label htmlFor="name">Name</label>
                  <input 
                    id="name"
                    className="modal-input" 
                    name="name" 
                    value={form.name||''} 
                    onChange={handleChange} 
                    disabled={isView} 
                    required 
                  />
                </div>
                <div>
                  <label htmlFor="email">Email</label>
                  <input 
                    id="email"
                    className="modal-input" 
                    name="email" 
                    value={form.email||''} 
                    onChange={handleChange} 
                    disabled={isView} 
                    required 
                  />
                </div>
                <div>
                  <label htmlFor="phone">Phone</label>
                  <input 
                    id="phone"
                    className="modal-input" 
                    name="phone" 
                    value={form.phone||''} 
                    onChange={handleChange} 
                    disabled={isView} 
                    required 
                    pattern="[0-9]{10,15}"
                    title="Please enter a valid phone number"
                  />
                </div>
                <div className="modal-row">
                  <div className="modal-col">
                    <label htmlFor="checkIn">Check-In</label>
                    <input 
                      id="checkIn"
                      className="modal-input" 
                      type="date" 
                      name="checkIn" 
                      value={form.checkIn||''} 
                      onChange={handleChange} 
                      disabled={isView} 
                      required 
                    />
                  </div>
                  <div className="modal-col">
                    <label htmlFor="checkOut">Check-Out</label>
                    <input 
                      id="checkOut"
                      className="modal-input" 
                      type="date" 
                      name="checkOut" 
                      value={form.checkOut||''} 
                      onChange={handleChange} 
                      disabled={isView} 
                      required 
                    />
                  </div>
                </div>
                <div className="modal-row">
                  <div className="modal-col">
                    <label htmlFor="bedType">Bed Type</label>
                    <select 
                      id="bedType"
                      className="modal-input" 
                      name="bedType" 
                      value={form.bedType||''} 
                      onChange={handleChange} 
                      disabled={isView} 
                      required 
                    >
                      <option value="Male Dormitory">Male Dormitory</option>
                      <option value="Female Dormitory">Female Dormitory</option>
                      <option value="Mixed Dormitory">Mixed Dormitory</option>
                    </select>
                  </div>
                  <div className="modal-col">
                    <label htmlFor="package">Package</label>
                    <select 
                      id="package"
                      className="modal-input" 
                      name="package" 
                      value={form.package||'Regular Plan - 12-24 Hours'} 
                      onChange={handleChange} 
                      disabled={isView} 
                      required 
                    >
                      <option value="Hourly Plan - 1 Hour">Hourly Plan - 1 Hour (₹199)</option>
                      <option value="Hourly Plan - 3 Hours">Hourly Plan - 3 Hours (₹349)</option>
                      <option value="Hourly Plan - 5 Hours">Hourly Plan - 5 Hours (₹499)</option>
                      <option value="Regular Plan - 12-24 Hours">Regular Plan - 12-24 Hours (₹599)</option>
                      <option value="Weekly Plan - 7 Days">Weekly Plan - 7 Days (₹3499)</option>
                      <option value="Monthly Plan - 30 Days">Monthly Plan - 30 Days (₹11999)</option>
                    </select>
                  </div>
                </div>
                <div className="modal-row">
                  <div className="modal-col">
                    <label htmlFor="beds">Number of Beds</label>
                    <input 
                      id="beds"
                      className="modal-input" 
                      type="number" 
                      min="1" 
                      max="38"
                      name="beds" 
                      value={form.beds||1} 
                      onChange={handleChange} 
                      disabled={isView} 
                      required 
                    />
                  </div>
                  <div className="modal-col">
                    <label htmlFor="pricePerBed">Price Per Bed</label>
                    <input 
                      id="pricePerBed"
                      className="modal-input" 
                      type="number" 
                      name="pricePerBed" 
                      value={form.pricePerBed||599} 
                      onChange={handleChange} 
                      disabled={isView ? true : false}
                      required 
                    />
                  </div>
                </div>
                <div className="modal-row">
                  <div className="modal-col" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <label htmlFor="applyConvenienceFee" style={{ margin: 0 }}>Apply Convenience Fee (5%)</label>
                    <input
                      id="applyConvenienceFee"
                      type="checkbox"
                      name="applyConvenienceFee"
                      checked={!!form.applyConvenienceFee}
                      onChange={handleChange}
                      disabled={isView}
                    />
                  </div>
                </div>
                <div className="modal-row">
                  <div className="modal-col">
                    <label>Bed Selection</label>
                    {!isView && (
                      <>
                        <button
                          type="button"
                          className="modal-btn modal-btn-secondary"
                          style={{ marginBottom: 8 }}
                          onClick={() => setShowBedSelector(true)}
                        >
                          {form.selectedBeds && form.selectedBeds.length > 0 ? 'Edit Selected Beds' : 'Select Beds'}
                        </button>
                        {showBedSelector && (
                          <div className="bed-selector-modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.3)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <div className="bed-selector-modal-content" style={{ background: '#fff', borderRadius: 16, padding: '2.2rem 2.5rem', minWidth: 320, width: '700px', maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto', margin: '4vh 0', boxShadow: '0 8px 32px rgba(0,0,0,0.18)', position: 'relative' }}>
                              <style>{`
                                @media (max-width: 900px) {
                                  .bed-selector-modal-content {
                                    width: 98vw !important;
                                    min-width: 0 !important;
                                    padding: 1.2rem 0.6rem !important;
                                  }
                                }
                                @media (max-width: 600px) {
                                  .bed-selector-modal-content {
                                    width: 100vw !important;
                                    padding: 0.7rem 0.1rem !important;
                                    border-radius: 8px !important;
                                  }
                                }
                              `}</style>
                              <h3 style={{ marginTop: 0, marginBottom: '1.5rem', textAlign: 'center' }}>Select {form.beds} Bed{form.beds > 1 ? 's' : ''} - {form.bedType}</h3>
                              <DormitoryLayout 
                                bedType={form.bedType}
                                checkInDate={form.checkIn}
                                checkOutDate={form.checkOut}
                                selectedBeds={form.selectedBeds}
                                bedsToSelect={parseInt(form.beds) || 1}
                                excludeBookingId={booking?.id}
                                onConfirm={(beds) => {
                                  setForm(prev => ({ ...prev, selectedBeds: beds }));
                                  setShowBedSelector(false);
                                }}
                                onCancel={() => setShowBedSelector(false)}
                              />
                              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', marginTop: '1.5rem' }}>
                                <button 
                                  type="button" 
                                  onClick={() => setShowBedSelector(false)}
                                  style={{ padding: '0.75rem 1.5rem', background: '#10b981', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
                                >
                                  Done ({form.selectedBeds?.length || 0} beds selected)
                                </button>
                                <button 
                                  type="button" 
                                  onClick={() => setShowBedSelector(false)}
                                  style={{ padding: '0.75rem 1.5rem', background: '#6b7280', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
                                >
                                  Cancel
                                </button>
                              </div>
                              <button 
                                type="button" 
                                onClick={() => setShowBedSelector(false)}
                                style={{ position: 'absolute', top: '1rem', right: '1rem', background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}
                              >
                                ×
                              </button>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                    <button 
                      type="button" 
                      onClick={() => setShowBedSelector(true)}
                      style={{ background: '#e2e8f0', border: 'none', borderRadius: '20px', padding: '0.5rem 1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                    >
                      <FaBed /> Select Beds ({form.selectedBeds ? form.selectedBeds.length : 0}/{parseInt(form.beds) || 1} selected)
                    </button>
                    {form.selectedBeds && form.selectedBeds.length > 0 && (
                      <div style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: '#64748b' }}>
                        Selected: {form.selectedBeds.map(bed => bed.display || bed.id).join(', ')}
                      </div>
                    )}
                  </div>
                </div>
                <div className="modal-row">
                  <div className="modal-col">
                    <label htmlFor="status">Status</label>
                    <select 
                      id="status"
                      className="modal-input" 
                      name="status" 
                      value={form.status||''} 
                      onChange={handleChange} 
                      disabled={isView} 
                      required 
                    >
                      <option value="pending">Pending</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="checked-in">Checked In</option>
                      <option value="checked-out">Checked Out</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                  <div className="modal-col">
                    <label htmlFor="payment">Payment</label>
                    <select 
                      id="payment"
                      className="modal-input" 
                      name="payment" 
                      value={form.payment||''} 
                      onChange={handleChange} 
                      disabled={isView} 
                      required 
                    >
                      <option value="pending">Pending</option>
                      <option value="partial">Partial</option>
                      <option value="paid">Paid</option>
                      <option value="refunded">Refunded</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label htmlFor="amount">Total Amount (₹)</label>
                  <input 
                    id="amount"
                    className="modal-input" 
                    type="number" 
                    min={0} 
                    step={0.01} 
                    name="amount" 
                    value={form.amount||''} 
                    onChange={handleChange} 
                    disabled={true}
                    required 
                    style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                  />
                  <small style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                    Calculated automatically based on package, beds, and dates
                  </small>
                </div>
                {form.payment === 'partial' && (
                  <div>
                    <label htmlFor="amountPaid">Amount Paid (₹)</label>
                    <input
                      id="amountPaid"
                      className="modal-input"
                      type="number"
                      min={0}
                      step={0.01}
                      name="amountPaid"
                      value={form.amountPaid || 0}
                      onChange={handleChange}
                      disabled={isView}
                      required
                    />
                  </div>
                )}
                <div>
                  <label>Extra Charges</label>
                  {form.extraCharges && form.extraCharges.length > 0 && form.extraCharges.map((charge, idx) => (
                    <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                      <input
                        type="text"
                        placeholder="Description (e.g. Towel)"
                        className="modal-input"
                        style={{ flex: 2 }}
                        value={charge.label}
                        onChange={e => {
                          if (isView) return;
                          const updated = [...form.extraCharges];
                          updated[idx].label = e.target.value;
                          setForm(f => ({ ...f, extraCharges: updated }));
                        }}
                        disabled={isView}
                        required
                      />
                      <input
                        type="number"
                        placeholder="Amount"
                        className="modal-input"
                        style={{ flex: 1 }}
                        value={charge.amount}
                        min={0}
                        step={0.01}
                        onChange={e => {
                          if (isView) return;
                          const updated = [...form.extraCharges];
                          updated[idx].amount = parseFloat(e.target.value) || 0;
                          setForm(f => ({ ...f, extraCharges: updated }));
                          // Recalculate total amount when extra charges change
                          handleChange({ target: { name: 'extraCharges', value: updated } });
                        }}
                        disabled={isView}
                        required
                      />
                      {!isView && (
                        <button type="button" className="modal-btn modal-btn-secondary" style={{ marginLeft: 2, padding: '0.35rem 0.9rem', fontSize: '0.95rem', height: '2.2rem' }} onClick={() => {
                          setForm(f => ({ ...f, extraCharges: f.extraCharges.filter((_, i) => i !== idx) }));
                        }}>Remove</button>
                      )}
                    </div>
                  ))}
                  {!isView && (
                    <button type="button" className="modal-btn modal-btn-secondary" onClick={() => setForm(f => ({ ...f, extraCharges: [...(f.extraCharges||[]), { label: '', amount: 0 }] }))} style={{ marginTop: 4, fontSize: 13, padding: '0.35rem 0.9rem', height: '2.2rem' }}>+ Add Extra Charge</button>
                  )}
                </div>
                {/* Action buttons - only show in add/edit mode */}
                {!isView && (
                  <div className="modal-actions">
                    <button 
                      type="button" 
                      className="modal-btn modal-btn-secondary" 
                      onClick={() => navigate('/admin/dashboard')}
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      className="modal-btn modal-btn-primary"
                      onClick={(e) => {
                        console.log('🚨 BUTTON CLICK DETECTED');
                        console.log('🚨 Event:', e);
                        console.log('🚨 Form element:', e.target.form);
                        // Let the form submission handle the rest
                      }}
                    >
                      {mode === 'add' ? 'Add Booking' : 'Update Booking'}
                    </button>
                  </div>
                )}
                {/* View mode buttons */}
                {isView && (
                  <div className="modal-actions">
                    <button 
                      type="button" 
                      className="modal-btn modal-btn-secondary" 
                      onClick={handleDownloadInvoice}
                    >
                      Download Invoice
                    </button>
                    <button 
                      type="button" 
                      className="modal-btn modal-btn-secondary" 
                      onClick={() => navigate('/admin/dashboard')}
                    >
                      Close
                    </button>
                  </div>
                )}
              </form>
            </div>
            {/* Booking Preview/Details Section */}
            {(!isView) && (
              <div className="modal-summary-section">
                <div className="modal-summary-title">
                  Booking Preview
                </div>
                <div className="modal-summary-content">
                  <div className="modal-summary-row">
                    <span className="modal-summary-label">Guest:</span>
                    <span className="modal-summary-value">{form.name || '---'}</span>
                  </div>
                  <div className="modal-summary-row">
                    <span className="modal-summary-label">Email:</span>
                    <span className="modal-summary-value">{form.email || '---'}</span>
                  </div>
                  <div className="modal-summary-row">
                    <span className="modal-summary-label">Phone:</span>
                    <span className="modal-summary-value">{form.phone || '---'}</span>
                  </div>
                  <div className="modal-summary-row">
                    <span className="modal-summary-label">Dates:</span>
                    <span className="modal-summary-value modal-summary-value-right">
                      {form.checkIn ? new Date(form.checkIn).toLocaleDateString() : '---'}<br/>
                      to {form.checkOut ? new Date(form.checkOut).toLocaleDateString() : '---'}
                    </span>
                  </div>
                  <div className="modal-summary-row">
                    <span className="modal-summary-label">Bed Details:</span>
                    <span className="modal-summary-value modal-summary-value-right">
                      {form.selectedBeds && form.selectedBeds.length > 0 ? 
                        `${form.selectedBeds.length} ${form.bedType} beds (${form.selectedBeds.length >= 38 ? 'All beds' : form.selectedBeds.map(bed => bed.display || bed.id || bed).join(', ')})` :
                        `${form.beds || 0} ${form.bedType || '---'} bed${form.beds !== 1 ? 's' : ''}`
                      }
                    </span>
                  </div>
                  <div className="modal-summary-row">
                    <span className="modal-summary-label">Status:</span>
                    <span className="modal-summary-value">{form.status || '---'}</span>
                  </div>
                  <div className="modal-summary-row">
                    <span className="modal-summary-label">Payment:</span>
                    <span className="modal-summary-value">{form.payment || '---'}</span>
                  </div>
                  <div className="modal-summary-row">
                    <span className="modal-summary-label">Package:</span>
                    <span className="modal-summary-value">{form.package || 'Regular Plan - 12-24 Hours'}</span>
                  </div>
                  <div className="modal-summary-row">
                    <span className="modal-summary-label">Rate per bed:</span>
                    <span className="modal-summary-value">₹{form.pricePerBed || 599}</span>
                  </div>
                  <div className="modal-summary-row">
                    <span className="modal-summary-label">Subtotal:</span>
                    <span className="modal-summary-value">₹{
                      (() => {
                        const beds = parseInt(form.beds) || 1;
                        const pricePerBed = form.pricePerBed || 599;
                        const packageName = form.package || '';
                        const isHourly = packageName.includes('Hourly Plan');
                        const isWeekly = packageName.includes('Weekly Plan');
                        const isMonthly = packageName.includes('Monthly Plan');
                        
                        if (isHourly || isWeekly || isMonthly) {
                          return pricePerBed * beds;
                        } else {
                          const checkIn = form.checkIn ? new Date(form.checkIn) : null;
                          const checkOut = form.checkOut ? new Date(form.checkOut) : null;
                          const nights = checkIn && checkOut ? Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24)) : 1;
                          return pricePerBed * beds * Math.max(nights, 1);
                        }
                      })()
                    }</span>
                  </div>
                  {(form.extraCharges || []).length > 0 && (form.extraCharges || []).map((charge, idx) => (
                    <div key={idx} className="modal-summary-row">
                      <span className="modal-summary-label">{charge.label || `Extra Charge ${idx + 1}`}:</span>
                      <span className="modal-summary-value">₹{charge.amount || 0}</span>
                    </div>
                  ))}
                  {form.applyConvenienceFee && (
                    <div className="modal-summary-row">
                      <span className="modal-summary-label">Convenience Fee ({((form.convenienceFeeRate||0.05)*100).toFixed(0)}%):</span>
                      <span className="modal-summary-value">₹{
                        (() => {
                          const beds = parseInt(form.beds) || 1;
                          const pricePerBed = form.pricePerBed || 599;
                          const packageName = form.package || '';
                          const isHourly = packageName.includes('Hourly Plan');
                          const isWeekly = packageName.includes('Weekly Plan');
                          const isMonthly = packageName.includes('Monthly Plan');
                          
                          let subtotal;
                          if (isHourly || isWeekly || isMonthly) {
                            subtotal = pricePerBed * beds;
                          } else {
                            const checkIn = form.checkIn ? new Date(form.checkIn) : null;
                            const checkOut = form.checkOut ? new Date(form.checkOut) : null;
                            const nights = checkIn && checkOut ? Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24)) : 1;
                            subtotal = pricePerBed * beds * Math.max(nights, 1);
                          }
                          return Math.round(subtotal * (form.convenienceFeeRate||0.05));
                        })()
                      }</span>
                    </div>
                  )}
                  <div className="modal-summary-total">
                    <span className="modal-summary-total-label">Total:</span>
                    <span className="modal-summary-total-value">₹{
                      (() => {
                        const beds = parseInt(form.beds) || 1;
                        const pricePerBed = form.pricePerBed || 599;
                        const packageName = form.package || '';
                        const isHourly = packageName.includes('Hourly Plan');
                        const isWeekly = packageName.includes('Weekly Plan');
                        const isMonthly = packageName.includes('Monthly Plan');
                        
                        let subtotal;
                        if (isHourly || isWeekly || isMonthly) {
                          subtotal = pricePerBed * beds;
                        } else {
                          const checkIn = form.checkIn ? new Date(form.checkIn) : null;
                          const checkOut = form.checkOut ? new Date(form.checkOut) : null;
                          const nights = checkIn && checkOut ? Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24)) : 1;
                          subtotal = pricePerBed * beds * Math.max(nights, 1);
                        }
                        const extraChargesTotal = (form.extraCharges || []).reduce((sum, charge) => sum + (parseFloat(charge.amount) || 0), 0);
                        const convenienceFee = form.applyConvenienceFee ? Math.round(subtotal * (form.convenienceFeeRate||0.05)) : 0;
                        return subtotal + extraChargesTotal + convenienceFee;
                      })()
                    }</span>
                  </div>
                </div>
              </div>
            )}
            {/* View mode - Full details */}
            {isView && (
              <div className="modal-summary-section">
                <div className="modal-summary-title">
                  Booking Details
                </div>
                <div className="modal-summary-content">
                  <div className="modal-summary-row">
                    <span className="modal-summary-label">Booking ID:</span>
                    <span className="modal-summary-value">{booking?.id || '---'}</span>
                  </div>
                  <div className="modal-summary-row">
                    <span className="modal-summary-label">Guest:</span>
                    <span className="modal-summary-value">{form.name || '---'}</span>
                  </div>
                  <div className="modal-summary-row">
                    <span className="modal-summary-label">Email:</span>
                    <span className="modal-summary-value">{form.email || '---'}</span>
                  </div>
                  <div className="modal-summary-row">
                    <span className="modal-summary-label">Phone:</span>
                    <span className="modal-summary-value">{form.phone || '---'}</span>
                  </div>
                  <div className="modal-summary-row">
                    <span className="modal-summary-label">Dates:</span>
                    <span className="modal-summary-value modal-summary-value-right">
                      {form.checkIn ? new Date(form.checkIn).toLocaleDateString() : '---'}<br/>
                      to {form.checkOut ? new Date(form.checkOut).toLocaleDateString() : '---'}
                    </span>
                  </div>
                  <div className="modal-summary-row">
                    <span className="modal-summary-label">Bed Details:</span>
                    <span className="modal-summary-value modal-summary-value-right">
                      {form.selectedBeds && form.selectedBeds.length > 0 ? 
                        `${form.selectedBeds.length} ${form.bedType} beds (${form.selectedBeds.length >= 38 ? 'All beds' : form.selectedBeds.map(bed => bed.display || bed.id || bed).join(', ')})` :
                        `${form.beds || 0} ${form.bedType || '---'} bed${form.beds !== 1 ? 's' : ''}`
                      }
                    </span>
                  </div>
                  <div className="modal-summary-row">
                    <span className="modal-summary-label">Status:</span>
                    <span className="modal-summary-value">{form.status || '---'}</span>
                  </div>
                  <div className="modal-summary-row">
                    <span className="modal-summary-label">Payment:</span>
                    <span className="modal-summary-value">{form.payment || '---'}</span>
                  </div>
                  <div className="modal-summary-row">
                    <span className="modal-summary-label">Package:</span>
                    <span className="modal-summary-value">{form.package || 'Regular Plan - 12-24 Hours'}</span>
                  </div>
                  <div className="modal-summary-row">
                    <span className="modal-summary-label">Rate per bed:</span>
                    <span className="modal-summary-value">₹{form.pricePerBed || 599}</span>
                  </div>
                  <div className="modal-summary-row">
                    <span className="modal-summary-label">Subtotal:</span>
                    <span className="modal-summary-value">₹{
                      (() => {
                        const beds = parseInt(form.beds) || 1;
                        const pricePerBed = form.pricePerBed || 599;
                        const packageName = form.package || '';
                        const isHourly = packageName.includes('Hourly Plan');
                        const isWeekly = packageName.includes('Weekly Plan');
                        const isMonthly = packageName.includes('Monthly Plan');
                        
                        if (isHourly || isWeekly || isMonthly) {
                          return pricePerBed * beds;
                        } else {
                          const checkIn = form.checkIn ? new Date(form.checkIn) : null;
                          const checkOut = form.checkOut ? new Date(form.checkOut) : null;
                          const nights = checkIn && checkOut ? Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24)) : 1;
                          return pricePerBed * beds * Math.max(nights, 1);
                        }
                      })()
                    }</span>
                  </div>
                  {(form.extraCharges || []).length > 0 && (form.extraCharges || []).map((charge, idx) => (
                    <div key={idx} className="modal-summary-row">
                      <span className="modal-summary-label">{charge.label || `Extra Charge ${idx + 1}`}:</span>
                      <span className="modal-summary-value">₹{charge.amount || 0}</span>
                    </div>
                  ))}
                  {form.applyConvenienceFee && (
                    <div className="modal-summary-row">
                      <span className="modal-summary-label">Convenience Fee ({((form.convenienceFeeRate||0.05)*100).toFixed(0)}%):</span>
                      <span className="modal-summary-value">₹{
                        (() => {
                          const beds = parseInt(form.beds) || 1;
                          const pricePerBed = form.pricePerBed || 599;
                          const packageName = form.package || '';
                          const isHourly = packageName.includes('Hourly Plan');
                          const isWeekly = packageName.includes('Weekly Plan');
                          const isMonthly = packageName.includes('Monthly Plan');
                          
                          let subtotal;
                          if (isHourly || isWeekly || isMonthly) {
                            subtotal = pricePerBed * beds;
                          } else {
                            const checkIn = form.checkIn ? new Date(form.checkIn) : null;
                            const checkOut = form.checkOut ? new Date(form.checkOut) : null;
                            const nights = checkIn && checkOut ? Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24)) : 1;
                            subtotal = pricePerBed * beds * Math.max(nights, 1);
                          }
                          return Math.round(subtotal * (form.convenienceFeeRate||0.05));
                        })()
                      }</span>
                    </div>
                  )}
                  <div className="modal-summary-total">
                    <span className="modal-summary-total-label">Total:</span>
                    <span className="modal-summary-total-value">₹{
                      (() => {
                        const beds = parseInt(form.beds) || 1;
                        const pricePerBed = form.pricePerBed || 599;
                        const packageName = form.package || '';
                        const isHourly = packageName.includes('Hourly Plan');
                        const isWeekly = packageName.includes('Weekly Plan');
                        const isMonthly = packageName.includes('Monthly Plan');
                        
                        let subtotal;
                        if (isHourly || isWeekly || isMonthly) {
                          subtotal = pricePerBed * beds;
                        } else {
                          const checkIn = form.checkIn ? new Date(form.checkIn) : null;
                          const checkOut = form.checkOut ? new Date(form.checkOut) : null;
                          const nights = checkIn && checkOut ? Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24)) : 1;
                          subtotal = pricePerBed * beds * Math.max(nights, 1);
                        }
                        const extraChargesTotal = (form.extraCharges || []).reduce((sum, charge) => sum + (parseFloat(charge.amount) || 0), 0);
                        const convenienceFee = form.applyConvenienceFee ? Math.round(subtotal * (form.convenienceFeeRate||0.05)) : 0;
                        return subtotal + extraChargesTotal + convenienceFee;
                      })()
                    }</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}
